import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { metricService } from '../../services/metricService';
import PageHeader from '../common/PageHeader';
import './MetricBuilder.css';

interface MetricBuilderProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

interface MetricOption {
  id: string;
  name: string;
  description: string;
}

interface DimensionOption {
  id: string;
  label: string;
}

const MetricBuilder: React.FC<MetricBuilderProps> = ({ user, onClose, onToggleSidebar }) => {
  const navigate = useNavigate();
  const [selectedMetric, setSelectedMetric] = useState<string>('');
  const [selectedDimensions, setSelectedDimensions] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({ start: '', end: '' });
  const [habitNameFilter, setHabitNameFilter] = useState<string>('');
  const [habitCategoryFilter, setHabitCategoryFilter] = useState<string>('');
  
  const [metrics, setMetrics] = useState<MetricOption[]>([]);
  const [dimensions, setDimensions] = useState<DimensionOption[]>([]);
  const [habitNames, setHabitNames] = useState<string[]>([]);
  const [habitCategories, setHabitCategories] = useState<string[]>([]);
  
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [tableData, setTableData] = useState<{ columns: string[]; rows: any[][] } | null>(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [metricName, setMetricName] = useState<string>('');
  const [metricDescription, setMetricDescription] = useState<string>('');
  const [metricCategory, setMetricCategory] = useState<string>('habits');
  const [metricUnit, setMetricUnit] = useState<string>('count');
  const [isCustomUnit, setIsCustomUnit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [streakThreshold, setStreakThreshold] = useState<string>('1');
  const [streakPeriod, setStreakPeriod] = useState<'day' | 'week'>('week');

  useEffect(() => {
    if (user?.id) {
      loadOptions();
    }
  }, [user]);

  const loadOptions = async () => {
    try {
      setLoadingOptions(true);
      setError(null);
      
      const optionsResponse = await metricService.getMetricBuilderOptions();
      if (optionsResponse.success) {
        if (optionsResponse.metrics) setMetrics(optionsResponse.metrics);
        if (optionsResponse.dimensions) setDimensions(optionsResponse.dimensions);
      }
      
      const habitNamesResponse = await metricService.getFilterValues('habit_name');
      if (habitNamesResponse.success && habitNamesResponse.values) {
        setHabitNames(habitNamesResponse.values);
      }
      
      const habitCategoriesResponse = await metricService.getFilterValues('habit_category');
      if (habitCategoriesResponse.success && habitCategoriesResponse.values) {
        setHabitCategories(habitCategoriesResponse.values);
      }
    } catch (err) {
      console.error('Error loading options:', err);
      setError('Failed to load options. Please try again.');
    } finally {
      setLoadingOptions(false);
    }
  };

  const handleDimensionToggle = (dimensionId: string) => {
    setSelectedDimensions(prev => {
      if (prev.includes(dimensionId)) {
        return prev.filter(id => id !== dimensionId);
      } else {
        return [...prev, dimensionId];
      }
    });
  };

  const handleBuild = async () => {
    if (!selectedMetric) {
      setError('Please select a metric');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const filters: any = {};
      if (dateRange.start || dateRange.end) {
        filters.date_range = {};
        if (dateRange.start) filters.date_range.start = dateRange.start;
        if (dateRange.end) filters.date_range.end = dateRange.end;
      }
      if (habitNameFilter) {
        filters.habit_name = habitNameFilter;
      }
      if (habitCategoryFilter) {
        filters.habit_category = habitCategoryFilter;
      }
      
      const payload = {
        metric: selectedMetric,
        dimensions: selectedDimensions.length > 0 ? selectedDimensions : undefined,
        filters: Object.keys(filters).length > 0 ? filters : undefined,
        threshold: selectedMetric === 'habit_streak' ? (streakThreshold ? Number(streakThreshold) : 1) : undefined,
        period: selectedMetric === 'habit_streak' ? streakPeriod : undefined,
      };
      
      const response = await metricService.buildMetric(payload);
      
      if (response.success && response.columns && response.rows) {
        setTableData({
          columns: response.columns,
          rows: response.rows,
        });
      } else {
        setError(response.message || 'Failed to build metric');
      }
    } catch (err) {
      console.error('Error building metric:', err);
      setError('Failed to build metric. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedMetric('');
    setSelectedDimensions([]);
    setDateRange({ start: '', end: '' });
    setHabitNameFilter('');
    setHabitCategoryFilter('');
    setStreakThreshold('1');
    setStreakPeriod('week');
    setTableData(null);
    setError(null);
    setShowSaveDialog(false);
    setMetricName('');
    setMetricDescription('');
    setMetricCategory('habits');
    setMetricUnit('count');
    setIsCustomUnit(false);
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    if (!metricName.trim()) {
      setError('Please enter a metric name');
      return;
    }

    if (!selectedMetric) {
      setError('Please select a metric');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      const filters: any = {};
      if (dateRange.start || dateRange.end) {
        filters.date_range = {};
        if (dateRange.start) filters.date_range.start = dateRange.start;
        if (dateRange.end) filters.date_range.end = dateRange.end;
      }
      if (habitNameFilter) {
        filters.habit_name = habitNameFilter;
      }
      if (habitCategoryFilter) {
        filters.habit_category = habitCategoryFilter;
      }
      
      const payload = {
        name: metricName.trim(),
        description: metricDescription.trim() || undefined,
        category: metricCategory,
        unit: metricUnit,
        metric: selectedMetric,
        dimensions: selectedDimensions.length > 0 ? selectedDimensions : undefined,
        filters: Object.keys(filters).length > 0 ? filters : undefined,
        threshold: selectedMetric === 'habit_streak' ? (streakThreshold ? Number(streakThreshold) : 1) : undefined,
        period: selectedMetric === 'habit_streak' ? streakPeriod : undefined,
      };
      
      const response = await metricService.saveMetric(payload);
      
      if (response.success) {
        setSaveSuccess(true);
        setTimeout(() => {
          navigate('/metrics');
        }, 1500);
      } else {
        setError(response.message || 'Failed to save metric');
      }
    } catch (err: any) {
      console.error('Error saving metric:', err);
      setError(err.message || 'Failed to save metric. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="metric-builder-page">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1>Metric Builder</h1>}
        leftActions={
          <button
            onClick={() => navigate('/metrics')}
            className="metric-builder-back-button"
            title="Back to Metrics"
          >
            ← Back
          </button>
        }
      />

      {error && (
        <div className="metric-builder-error">
          {error}
          <button onClick={() => setError(null)} className="error-close-btn">×</button>
        </div>
      )}

      <div className="metric-builder-content">
        {loadingOptions ? (
          <div className="metric-builder-loading">
            <p>Loading options...</p>
          </div>
        ) : (
          <>
            <div className="metric-builder-form">
              <div className="metric-builder-section">
                <label className="metric-builder-label">Select Metric</label>
                <select
                  value={selectedMetric}
                  onChange={(e) => setSelectedMetric(e.target.value)}
                  className="metric-builder-select"
                >
                  <option value="">-- Select a metric --</option>
                  {metrics.map((metric) => (
                    <option key={metric.id} value={metric.id}>
                      {metric.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="metric-builder-section">
                <label className="metric-builder-label">Slice by Dimensions</label>
                <div className="metric-builder-dimensions">
                  {dimensions.map((dimension) => (
                    <label key={dimension.id} className="metric-builder-checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectedDimensions.includes(dimension.id)}
                        onChange={() => handleDimensionToggle(dimension.id)}
                        className="metric-builder-checkbox"
                      />
                      <span>{dimension.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="metric-builder-section">
                <label className="metric-builder-label">Filters</label>
                <div className="metric-builder-filters">
                  {selectedMetric === 'habit_streak' && (
                    <>
                      <div className="metric-builder-filter-row">
                        <label>Streak Threshold:</label>
                        <input
                          type="number"
                          min="1"
                          value={streakThreshold}
                          onChange={(e) => setStreakThreshold(e.target.value)}
                          className="metric-builder-input"
                        />
                      </div>
                      <div className="metric-builder-filter-row">
                        <label>Streak Period:</label>
                        <select
                          value={streakPeriod}
                          onChange={(e) => setStreakPeriod(e.target.value as 'day' | 'week')}
                          className="metric-builder-select"
                        >
                          <option value="day">Daily</option>
                          <option value="week">Weekly</option>
                        </select>
                      </div>
                    </>
                  )}
                  <div className="metric-builder-filter-row">
                    <label>Date Range Start:</label>
                    <input
                      type="date"
                      value={dateRange.start}
                      onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                      className="metric-builder-input"
                    />
                  </div>
                  <div className="metric-builder-filter-row">
                    <label>Date Range End:</label>
                    <input
                      type="date"
                      value={dateRange.end}
                      onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                      className="metric-builder-input"
                    />
                  </div>
                  <div className="metric-builder-filter-row">
                    <label>Habit Name:</label>
                    <select
                      value={habitNameFilter}
                      onChange={(e) => setHabitNameFilter(e.target.value)}
                      className="metric-builder-select"
                    >
                      <option value="">All Habits</option>
                      {habitNames.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="metric-builder-filter-row">
                    <label>Habit Category:</label>
                    <select
                      value={habitCategoryFilter}
                      onChange={(e) => setHabitCategoryFilter(e.target.value)}
                      className="metric-builder-select"
                    >
                      <option value="">All Categories</option>
                      {habitCategories.map((category) => (
                        <option key={category} value={category}>
                          {category.charAt(0).toUpperCase() + category.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="metric-builder-actions">
                <button
                  onClick={handleBuild}
                  disabled={!selectedMetric || loading}
                  className="metric-builder-button metric-builder-button-primary"
                >
                  {loading ? 'Building...' : 'Build Metric'}
                </button>
                <button
                  onClick={handleReset}
                  className="metric-builder-button metric-builder-button-secondary"
                >
                  Reset
                </button>
              </div>
            </div>

            {tableData && (
              <div className="metric-builder-results">
                <div className="metric-builder-results-header">
                  <h2 className="metric-builder-results-title">Results</h2>
                  <button
                    onClick={() => setShowSaveDialog(true)}
                    className="metric-builder-button metric-builder-button-primary"
                  >
                    Save Metric
                  </button>
                </div>
                <div className="metric-builder-table-container">
                  <table className="metric-builder-table">
                    <thead>
                      <tr>
                        {tableData.columns.map((column, index) => (
                          <th key={index}>{column}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.rows.length === 0 ? (
                        <tr>
                          <td colSpan={tableData.columns.length} className="metric-builder-empty-cell">
                            No data available
                          </td>
                        </tr>
                      ) : (
                        tableData.rows.map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {row.map((cell, cellIndex) => (
                              <td key={cellIndex}>
                                {typeof cell === 'number' ? cell.toLocaleString() : cell}
                              </td>
                            ))}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {showSaveDialog && (
              <div className="metric-builder-save-dialog-overlay" onClick={() => setShowSaveDialog(false)}>
                <div className="metric-builder-save-dialog" onClick={(e) => e.stopPropagation()}>
                  <h3 className="metric-builder-save-dialog-title">Save Metric</h3>
                  {saveSuccess ? (
                    <div className="metric-builder-save-success">
                      <p>Metric saved successfully! Redirecting to metrics page...</p>
                    </div>
                  ) : (
                    <>
                      <div className="metric-builder-save-form">
                        <div className="metric-builder-save-field">
                          <label>Metric Name *</label>
                          <input
                            type="text"
                            value={metricName}
                            onChange={(e) => setMetricName(e.target.value)}
                            placeholder="e.g., Weekly Reading Total"
                            className="metric-builder-input"
                          />
                        </div>
                        <div className="metric-builder-save-field">
                          <label>Description</label>
                          <textarea
                            value={metricDescription}
                            onChange={(e) => setMetricDescription(e.target.value)}
                            placeholder="Optional description"
                            className="metric-builder-textarea"
                            rows={3}
                          />
                        </div>
                        <div className="metric-builder-save-field-row">
                          <div className="metric-builder-save-field">
                            <label>Category</label>
                            <select
                              value={metricCategory}
                              onChange={(e) => setMetricCategory(e.target.value)}
                              className="metric-builder-select"
                            >
                              <option value="habits">Habits</option>
                              <option value="fitness">Fitness</option>
                              <option value="health">Health</option>
                              <option value="productivity">Productivity</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                          <div className="metric-builder-save-field">
                            <label>Unit</label>
                            <select
                              value={isCustomUnit ? 'custom' : metricUnit}
                              onChange={(e) => {
                                if (e.target.value === 'custom') {
                                  setIsCustomUnit(true);
                                  setMetricUnit('');
                                } else {
                                  setIsCustomUnit(false);
                                  setMetricUnit(e.target.value);
                                }
                              }}
                              className="metric-builder-select"
                            >
                              <option value="count">Count</option>
                              <option value="percentage">Percentage</option>
                              <option value="currency">Currency</option>
                              <option value="days">Days</option>
                              <option value="minutes">Minutes</option>
                              <option value="custom">Custom...</option>
                            </select>
                            {isCustomUnit && (
                              <input
                                type="text"
                                value={metricUnit}
                                onChange={(e) => setMetricUnit(e.target.value)}
                                placeholder="e.g., km, pages, hours"
                                className="metric-builder-input"
                                style={{ marginTop: '0.5rem' }}
                                autoFocus
                              />
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="metric-builder-save-actions">
                        <button
                          onClick={handleSave}
                          disabled={!metricName.trim() || saving}
                          className="metric-builder-button metric-builder-button-primary"
                        >
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => setShowSaveDialog(false)}
                          className="metric-builder-button metric-builder-button-secondary"
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MetricBuilder;

